import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Stack } from '@mui/material'

import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'

import { StructureActivityCode } from '@/core/models/establishment.model'
import locales from '@/assets/locales'

import { zodResolver } from '@hookform/resolvers/zod'

const schema = z.object({
  name: z.string().min(1),
  finess: z.string().optional(),
  address: z.string().optional(),
  zipCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
  preferredLanguage: z.string().optional(),
  structureActivity: z.string().optional(),
  isJoinableByPatients: z.boolean().optional(),
  ordoclicVidalLicense: z
    .object({
      id: z.string().optional(),
      secret: z.string().optional(),
    })
    .optional(),
  desmosConfig: z
    .object({
      apiUrl: z.string().optional(),
      identityApiUrl: z.string().optional(),
      login: z.string().optional(),
      password: z.string().optional(),
      finess: z.string().optional(),
    })
    .optional(),
})

export type EstablishmentFormData = z.infer<typeof schema>

export type EstablishmentFormProps = {
  id?: string
  initialValues?: EstablishmentFormData
  isUpdate?: boolean
  onSubmit: SubmitHandler<EstablishmentFormData>
}

export function EstablishmentForm({ id = 'establishmentForm', initialValues, onSubmit }: EstablishmentFormProps) {
  const { t } = useTranslation()

  const rhForm = useForm<EstablishmentFormData>({
    defaultValues: initialValues ?? {
      name: '',
      finess: '',
      address: '',
      zipCode: '',
      city: '',
      country: '',
      phone: '',
      preferredLanguage: '',
      structureActivity: '',
      isJoinableByPatients: false,
      ordoclicVidalLicense: {
        id: '',
        secret: '',
      },
      desmosConfig: {
        apiUrl: '',
        identityApiUrl: '',
        login: '',
        password: '',
        finess: '',
      },
    },
    resolver: zodResolver(schema),
  })
  const { reset } = rhForm

  useEffect(() => {
    if (initialValues) {
      reset(initialValues)
    }
  }, [initialValues, reset])

  return (
    <form id={id} onSubmit={rhForm.handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <TextInputController control={rhForm.control} fullWidth label={t('field.name.label')} name="name" id="name" />
        <TextInputController control={rhForm.control} fullWidth label={t('field.finess.label')} name="finess" id="finess" />
        <TextInputController control={rhForm.control} fullWidth label={t('field.address.label')} name="address" id="address" />
        <TextInputController control={rhForm.control} fullWidth label={t('field.zipCode.label')} name="zipCode" id="zipCode" />
        <TextInputController control={rhForm.control} fullWidth label={t('field.city.label')} name="city" id="city" />
        <TextInputController control={rhForm.control} fullWidth label={t('field.country.label')} name="country" id="country" />
        <PhoneNumberInputController control={rhForm.control} label={t('field.phone.label')} name="phone" />
        <SelectInputController
          control={rhForm.control}
          fullWidth
          label={t('field.preferredLanguage.label')}
          name="preferredLanguage"
          id="preferredLanguage"
          options={[
            { label: t('field.preferredLanguage.inheritCreator'), value: '' },
            ...Object.keys(locales).map((language) => ({ label: language.toUpperCase(), value: language })),
          ]}
        />
        <SelectInputController
          control={rhForm.control}
          fullWidth
          label={t('field.structureActivity.label')}
          name="structureActivity"
          id="structureActivity"
          options={Object.values(StructureActivityCode).map((code) => ({
            label: t(`field.structureActivity.options.${code}`),
            value: code,
          }))}
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.ordoclicVidalLicence.id.label')}
          name="ordoclicVidalLicense.id"
          id="ordoclicVidalLicense.id"
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.ordoclicVidalLicence.secret.label')}
          name="ordoclicVidalLicense.secret"
          id="ordoclicVidalLicense.secret"
        />

        {/* Desmos Configuration */}
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.desmosConfig.apiUrl.label')}
          name="desmosConfig.apiUrl"
          id="desmosConfig.apiUrl"
          placeholder="https://your-desmos-url.com"
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.desmosConfig.identityApiUrl.label')}
          name="desmosConfig.identityApiUrl"
          id="desmosConfig.identityApiUrl"
          placeholder="https://your-identity-url.com"
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.desmosConfig.login.label')}
          name="desmosConfig.login"
          id="desmosConfig.login"
          placeholder="login_utilisateur"
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.desmosConfig.password.label')}
          name="desmosConfig.password"
          id="desmosConfig.password"
          type="password"
          placeholder="password_utilisateur"
        />
        <TextInputController
          control={rhForm.control}
          fullWidth
          label={t('field.desmosConfig.finess.label')}
          name="desmosConfig.finess"
          id="desmosConfig.finess"
          placeholder="006076863"
        />
        <CheckboxInputController control={rhForm.control} label={t('field.isJoinableByPatients.label')} name="isJoinableByPatients" id="isJoinableByPatients" />
      </Stack>
    </form>
  )
}
